import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import { complianceService } from "@/app/_module/services/complianceService";
import type { ComplianceDashboard } from "@/app/_module/types/compliance";

// Query keys for compliance data
// NOTE: Compliance is GLOBAL for system admins - no agency context needed
// Compliance dashboard monitors platform-wide compliance (HIPAA, SOC 2, etc.) for entire system
// Unlike other admin features, compliance monitoring is not agency-specific
export const complianceKeys = {
  all: () => ["compliance"] as const,
  dashboard: () => ["compliance", "dashboard"] as const,
  frameworks: () => ["compliance", "frameworks"] as const,
  frameworkById: (id: string) => ["compliance", "frameworks", id] as const,
  controls: () => ["compliance", "controls"] as const,
  controlById: (id: string) => ["compliance", "controls", id] as const,
  alerts: () => ["compliance", "alerts"] as const,
  history: () => ["compliance", "history"] as const,
};

/**
 * Fetch compliance dashboard data
 * Automatically refetches on mount and agency changes
 * Only accessible by System Admins
 */
export const useComplianceDashboard = (
  options?: Omit<UseQueryOptions<ComplianceDashboard, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: complianceKeys.dashboard(),
    queryFn: async () => {
      const data = await complianceService.getDashboard();
      return data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
    refetchOnMount: true, // Always refetch on mount
    refetchOnWindowFocus: true, // Refetch when user returns to tab
    retry: (failureCount, error) => {
      // Don't retry on 403 errors (permission denied)
      if (error?.message?.includes('403') || error?.message?.includes('insufficient permissions')) {
        return false;
      }
      return failureCount < 2;
    },
    ...options,
  });
};

/**
 * Fetch all compliance frameworks
 */
export const useComplianceFrameworks = (
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: complianceKeys.frameworks(),
    queryFn: async () => {
      const data = await complianceService.getFrameworks();
      return data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    ...options,
  });
};

/**
 * Fetch compliance framework by ID
 */
export const useComplianceFrameworkById = (
  id: string,
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: complianceKeys.frameworkById(id),
    queryFn: async () => {
      const data = await complianceService.getFrameworkDetails(id);
      return data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
    ...options,
  });
};

/**
 * Fetch compliance alerts
 */
export const useComplianceAlerts = (
  options?: Omit<UseQueryOptions<any, Error>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: complianceKeys.alerts(),
    queryFn: async () => {
      const data = await complianceService.getAlerts();
      return data;
    },
    staleTime: 1 * 60 * 1000, // 1 minute for alerts
    refetchOnMount: true,
    refetchOnWindowFocus: true,
    ...options,
  });
};

/**
 * Mutation hook for creating compliance framework
 */
export const useCreateFramework = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: any) => {
      return await complianceService.createFramework(data);
    },
    onSuccess: () => {
      // Invalidate and refetch frameworks
      queryClient.invalidateQueries({ queryKey: complianceKeys.frameworks() });
      queryClient.invalidateQueries({ queryKey: complianceKeys.dashboard() });
    },
  });
};

/**
 * Mutation hook for updating compliance framework
 */
export const useUpdateFramework = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      return await complianceService.updateFramework(id, data);
    },
    onSuccess: (_, variables) => {
      // Invalidate specific framework and list
      queryClient.invalidateQueries({ queryKey: complianceKeys.frameworkById(variables.id) });
      queryClient.invalidateQueries({ queryKey: complianceKeys.frameworks() });
      queryClient.invalidateQueries({ queryKey: complianceKeys.dashboard() });
    },
  });
};

/**
 * Mutation hook for deleting compliance framework
 */
export const useDeleteFramework = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      return await complianceService.deleteFramework(id);
    },
    onSuccess: () => {
      // Invalidate frameworks list
      queryClient.invalidateQueries({ queryKey: complianceKeys.frameworks() });
      queryClient.invalidateQueries({ queryKey: complianceKeys.dashboard() });
    },
  });
};

/**
 * Mutation hook for marking an alert as read
 */
export const useMarkAlertAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId: string) => {
      return await complianceService.markAlertAsRead(alertId);
    },
    onSuccess: () => {
      // Invalidate alerts and dashboard to refresh data
      queryClient.invalidateQueries({ queryKey: complianceKeys.alerts() });
      queryClient.invalidateQueries({ queryKey: complianceKeys.dashboard() });
    },
  });
};

/**
 * Mutation hook for marking all alerts as read
 */
export const useMarkAllAlertsAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return await complianceService.markAllAlertsAsRead();
    },
    onSuccess: () => {
      // Invalidate alerts and dashboard to refresh data
      queryClient.invalidateQueries({ queryKey: complianceKeys.alerts() });
      queryClient.invalidateQueries({ queryKey: complianceKeys.dashboard() });
    },
  });
};
